import axios from "axios";
import { useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import BASE_URL from "../constants/baseURL";
import { alert } from "../redux/slices/alertSlice";
import { apiError } from "../redux/slices/loaderSlice";
import { deleteAccessToken } from "../redux/slices/memberSlice";
import generateHmacSignature from "../utils/generateSignature";

export default function useAxios({
  baseURL = "adn_api",
  customBaseURL = "",
  customAuthToken = "",
  disableAuthToken = false,
} = {}) {
  const dispatch = useDispatch();
  const { authToken } = useSelector((state) => state.memberDetails);
  const token = sessionStorage.getItem("authToken");
  const axiosInstance = useRef(axios.create());

  useEffect(() => {
    axiosInstance.current = axios.create({
      headers: {
        ...(!disableAuthToken && {
          Authorization: customAuthToken || authToken || token,
        }),
        "content-type": "application/json",
      },

      ...(Boolean(customBaseURL)
        ? { baseURL: customBaseURL }
        : {
            baseURL: BASE_URL[baseURL].url + BASE_URL[baseURL].version,
          }),

      validateStatus: (status) => status < 500,
    });
  }, [
    authToken,
    baseURL,
    customAuthToken,
    customBaseURL,
    token,
    disableAuthToken,
  ]);

  return useCallback(
    async ({
      url,
      method = "GET",
      data,
      params,
      disableRedirect = false,
      disableError = false,
    }) => {
      try {
        const signature = await generateHmacSignature(data);

        return axiosInstance
          .current({
            headers: {
              ...signature,
            },
            url: url,
            method: method,
            data: data,
            params: params,
          })
          .then((response) => {
            let message = [];

            if (Array.isArray(response.data.error)) {
              response.data.error.map((error) => {
                message.push(error.msg);
                return message;
              });
            }

            if (response.data.detail?.code === "#401") {
              dispatch(
                alert({
                  message:
                    "Could not validate your credentials. Please log in again",
                  type: "error",
                })
              );
              return dispatch(deleteAccessToken());
            }

            if (
              response.status === 401 ||
              response.statusText === "Unauthorized"
            ) {
              dispatch(
                alert({
                  message: response.data.error,
                  type: "error",
                })
              );

              return dispatch(deleteAccessToken());
            }

            if (response.status > 201 || !response.data.status) {
              if (!disableError) {
                if (message.length > 0) {
                  dispatch(
                    alert({
                      multiple: message,
                      type: "error",
                    })
                  );
                } else {
                  dispatch(
                    alert({
                      message:
                        response.data?.error ||
                        response.data?.message?.displayMessage ||
                        "Error loading data!",
                      type: "error",
                    })
                  );
                }
              }

              if (!disableRedirect) {
                dispatch(apiError(response.data?.message?.traceID));

                // throw new Error();
                return {
                  status: false,
                  message:
                    message.length > 0
                      ? message
                      : response.data.error ||
                        response.data?.message?.displayMessage,
                };
              } else {
                return {
                  status: false,
                  message:
                    message.length > 0
                      ? message
                      : response.data.error ||
                        response.data?.message?.displayMessage,
                };
              }
            }

            return response.data;
          });
      } catch (error) {
        dispatch(
          alert({
            message: "Unable to connect to server. Please try again later.",
            type: "error",
          })
        );
        return {
          status: false,
          message: "Unable to connect to server. Please try again later.",
        };
      }
    },
    [dispatch]
  );
}
